import { useEffect, useMemo, useRef } from 'react';
import { DOC, REGION_COLORS, REGION_LABELS, TOKEN_STREAM, type Region } from './document';

const ACCENT = '#FF7A45';
const MUTED = 'rgba(232,232,234,0.45)';
const RULE = '#26272A';
const PAPER = '#0F0F11';

const CYCLE = 13;
const CHAR_PER_SEC = 55;
const HOLD_END = 1.6;

// Self-correction: while typing the labor chunk, hesitate on hba1c — type "7.2"
// then back up; the loop then types the correct "4" from the source.
const RETHINK = { chunkIdx: 7, afterText: '  "hba1c": 7.', wrong: '2' };

type TimelineEvent = { t: number; text: string; region: Region };

function buildTimeline(): { events: TimelineEvent[]; totalT: number } {
  const events: TimelineEvent[] = [];
  let t = 0;
  let buf = '';
  const dt = 1 / CHAR_PER_SEC;

  const push = (region: Region) => events.push({ t, text: buf, region });

  TOKEN_STREAM.forEach((chunk, ci) => {
    // Tiny "attention shift" pause when the model switches region.
    t += 0.12;
    push(chunk.region);

    for (let i = 0; i < chunk.text.length; i++) {
      buf += chunk.text[i];
      t += dt;
      push(chunk.region);

      // Trigger rethink right after the matching prefix.
      if (
        ci === RETHINK.chunkIdx &&
        buf.endsWith(RETHINK.afterText) &&
        !events.some((e) => e.text.endsWith(RETHINK.afterText + RETHINK.wrong))
      ) {
        // Type the wrong char.
        buf += RETHINK.wrong;
        t += dt;
        push(chunk.region);
        // Hold (model "second-guesses").
        t += 0.32;
        push(chunk.region);
        // Delete it.
        buf = buf.slice(0, -1);
        t += 0.06;
        push(chunk.region);
        // Think pause — next loop iteration types the correct char from source.
        t += 0.18;
        push(chunk.region);
      }
    }
  });

  return { events, totalT: t };
}

function visibleAt(events: TimelineEvent[], t: number): TimelineEvent {
  // Linear scan from the end — fast enough for ~700 events.
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    if (e.t <= t) return e;
  }
  return events[0]!;
}

type Props = { cycleSeconds?: number };

export default function OcrMotionC({ cycleSeconds = CYCLE }: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const timeline = useMemo(buildTimeline, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let widthCss = 0;
    let heightCss = 0;
    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      widthCss = rect.width;
      heightCss = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(widthCss * dpr);
      canvas.height = Math.floor(heightCss * dpr);
      canvas.style.width = `${widthCss}px`;
      canvas.style.height = `${heightCss}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const start = performance.now();
    let rafId = 0;

    const fontFor = (em: string | undefined) => {
      if (em === 'title') return '600 13px "Inter", system-ui, sans-serif';
      if (em === 'subtitle') return '500 11px "Inter", system-ui, sans-serif';
      if (em === 'small') return 'italic 10px "Inter", system-ui, sans-serif';
      return '11px "Inter", system-ui, sans-serif';
    };
    const lineHFor = (em: string | undefined) =>
      em === 'title' ? 22 : em === 'subtitle' ? 18 : 17;

    const draw = (now: number) => {
      const t = (now - start) / 1000;
      const W = widthCss;
      const H = heightCss;
      ctx.clearRect(0, 0, W, H);

      const padX = 20;
      const padY = 24;
      const gap = 24;
      const docW = (W - padX * 2 - gap) * 0.5;
      const trW = W - padX * 2 - gap - docW;
      const docX = padX;
      const docY = padY;
      const docH = H - padY * 2 - 22;
      const trX = padX + docW + gap;
      const trY = padY;
      const trH = docH;

      // Document.
      ctx.fillStyle = PAPER;
      ctx.fillRect(docX, docY, docW, docH);
      ctx.strokeStyle = RULE;
      ctx.lineWidth = 1;
      ctx.strokeRect(docX + 0.5, docY + 0.5, docW - 1, docH - 1);

      // Transcript.
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.fillRect(trX, trY, trW, trH);
      ctx.strokeRect(trX + 0.5, trY + 0.5, trW - 1, trH - 1);

      const innerX = docX + 18;
      const innerY = docY + 22;
      const innerW = docW - 36;

      const lineYs: number[] = [];
      let yCursor = innerY + 4;
      for (const l of DOC) {
        lineYs.push(yCursor);
        yCursor += lineHFor(l.emphasis);
      }

      // Cycle clock.
      const cycT = reduced ? timeline.totalT : t % (timeline.totalT + HOLD_END);
      const ev = visibleAt(timeline.events, Math.min(cycT, timeline.totalT));

      // Source text — render with subtle attention overlay on active region.
      DOC.forEach((line, i) => {
        ctx.fillStyle =
          line.emphasis === 'title' ? 'rgba(255,255,255,0.92)' : 'rgba(232,232,234,0.78)';
        ctx.font = fontFor(line.emphasis);
        ctx.textBaseline = 'top';
        ctx.fillText(line.text, innerX, lineYs[i]!);
      });

      // Attention rectangle — group consecutive lines that match active region.
      const activeRegion = ev.region;
      let aStart = -1;
      let aEnd = -1;
      for (let i = 0; i < DOC.length; i++) {
        if (DOC[i]!.region === activeRegion) {
          if (aStart < 0) aStart = i;
          aEnd = i;
        }
      }
      if (aStart >= 0) {
        const top = lineYs[aStart]! - 4;
        const bot = lineYs[aEnd]! + lineHFor(DOC[aEnd]!.emphasis) - 2;
        const color = REGION_COLORS[activeRegion];
        const pulse = 0.5 + 0.5 * Math.sin(t * 6);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.25;
        ctx.globalAlpha = 0.55 + 0.35 * pulse;
        ctx.strokeRect(innerX - 6, top, innerW + 12, bot - top);
        // Faint fill.
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.08 + 0.05 * pulse;
        ctx.fillRect(innerX - 6, top, innerW + 12, bot - top);
        ctx.globalAlpha = 1;

        // Attention arrow from box to transcript edge.
        const arrowY = top + (bot - top) / 2;
        ctx.strokeStyle = color;
        ctx.globalAlpha = 0.25;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 4]);
        ctx.beginPath();
        ctx.moveTo(innerX + innerW + 6, arrowY);
        ctx.lineTo(trX - 4, arrowY);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;

        // Region chip on the box.
        const label = REGION_LABELS[activeRegion];
        ctx.font = '600 9px "JetBrains Mono", ui-monospace, monospace';
        const lw = ctx.measureText(label).width;
        const chipW = lw + 10;
        const chipX = innerX + innerW + 12 - chipW;
        const chipY = top + 2;
        ctx.fillStyle = color;
        ctx.fillRect(chipX, chipY, chipW, 12);
        ctx.fillStyle = '#0B0B0C';
        ctx.fillText(label, chipX + 5, chipY + 2);
      }

      // Transcript — render full visible buffer.
      const trPadX = 16;
      const trPadY = 22;
      ctx.font = '11px "JetBrains Mono", ui-monospace, monospace';
      ctx.textBaseline = 'top';
      const lines = ev.text.split('\n');
      let ty = trY + trPadY;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]!;
        renderJsonLine(ctx, line, trX + trPadX, ty);
        // Cursor on the last line while typing.
        if (i === lines.length - 1 && cycT < timeline.totalT && !reduced) {
          const w = ctx.measureText(line).width;
          const blink = Math.floor(t * 2.4) % 2 === 0;
          if (blink) {
            ctx.fillStyle = ACCENT;
            ctx.fillRect(trX + trPadX + w + 1, ty + 1, 6, 12);
          }
        }
        ty += 14;
        if (ty > trY + trH - 24) break;
      }

      // Labels.
      ctx.fillStyle = MUTED;
      ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
      ctx.fillText('VLM · INPUT IMAGE', docX, docY - 10);
      ctx.fillText('OUTPUT · JSON STREAM', trX, trY - 10);
      const tps = Math.floor(CHAR_PER_SEC * (0.85 + 0.3 * Math.sin(t * 1.7)));
      ctx.fillStyle = ACCENT;
      const status = `${tps} tok/s`;
      const sw = ctx.measureText(status).width;
      ctx.fillText(status, trX + trW - sw, trY - 10);

      // Footer.
      const fY = docY + docH + 6;
      ctx.fillStyle = MUTED;
      ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
      ctx.fillText(
        `vlm: nougat-de-7b   stream   ctx: 4096   attn → ${REGION_LABELS[activeRegion].toLowerCase()}   gpu: de-fra1-h100`,
        docX,
        fY,
      );

      if (!reduced) rafId = requestAnimationFrame(draw);
    };

    if (reduced) draw(start);
    else rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [cycleSeconds, timeline]);

  return (
    <div ref={wrapRef} className="relative aspect-[16/7] w-full bg-black">
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}

function renderJsonLine(ctx: CanvasRenderingContext2D, text: string, x: number, y: number) {
  const KEY = '#FF7A45';
  const STR = '#E8E8EA';
  const NUM = '#34D399';
  const PUNCT = 'rgba(232,232,234,0.45)';

  const wsMatch = text.match(/^\s*/);
  const ws = wsMatch ? wsMatch[0] : '';
  let cx = x + ctx.measureText(ws).width;
  let rest = text.slice(ws.length);

  const keyMatch = rest.match(/^"([^"]*)"\s*:\s*/);
  if (keyMatch) {
    ctx.fillStyle = KEY;
    ctx.fillText(`"${keyMatch[1]}"`, cx, y);
    cx += ctx.measureText(`"${keyMatch[1]}"`).width;
    ctx.fillStyle = PUNCT;
    const colonPart = keyMatch[0].slice(keyMatch[1]!.length + 2);
    ctx.fillText(colonPart, cx, y);
    cx += ctx.measureText(colonPart).width;
    rest = rest.slice(keyMatch[0].length);
  }

  const tokenRe = /"([^"]*)"|(-?\d+(?:\.\d+)?)|([{}[\],])|(\s+)|([^"{}[\],\s]+)/g;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(rest))) {
    const [tok, str, num, punct, _ws, other] = m;
    let color = STR;
    if (str !== undefined) color = STR;
    else if (num !== undefined) color = NUM;
    else if (punct !== undefined) color = PUNCT;
    else if (other !== undefined) color = STR;
    ctx.fillStyle = color;
    ctx.fillText(tok, cx, y);
    cx += ctx.measureText(tok).width;
  }
}
